import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './index.scss';
import * as Bixby from '$/core';
import Modal from '$/components/global/modal';
import NoDataFound from '$/components/global/noData';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { ReactComponent as PencilIcon } from '$/assets/icons/penciledit.svg';
import { PrivateEquitySponsorService } from '$/services/PrivateEquitySponsorService';
import AddSponsor, { AddPrivateEquitySponsorValues } from '$/pages/privateEquitySponsors/add-sponsor';

const PrivateEquitySponsorsPage: React.FC = (props: any) => {
  const { dispatchToastSuccess, dispatchToastError } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [privateEquitySponsorsError, setPrivateEquitySponsorsError] = useState(undefined as string | undefined);
  const [privateEquitySponsors, setPrivateEquitySponsors] = useState(
    undefined as Bixby.Models.PrivateEquitySponsor[] | undefined
  );
  const [modalActive, setModalActive] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [updateValue, setUpdateValue] = useState<Bixby.Models.PrivateEquitySponsor>();

  useEffect(() => {
    const getEquitySponsors = async () => {
      setIsLoading(true);
      PrivateEquitySponsorService.getPrivateEquitySponsors()
        .then(result => setPrivateEquitySponsors(result))
        .catch(error => setPrivateEquitySponsorsError(error))
        .finally(() => setIsLoading(false));
    };
    getEquitySponsors();
  }, []);

  const handleSponsorEdit = (sponsor: Bixby.Models.PrivateEquitySponsor) => {
    setUpdateValue({ ...sponsor });
    setIsUpdate(true);
    setModalActive(true);
  };

  const handleSponsorDelete = async (sponsorId: string) => {
    setIsLoading(true);
    PrivateEquitySponsorService.deletePrivateEquitySponsors(sponsorId)
      .then(() => {
        PrivateEquitySponsorService.getPrivateEquitySponsors()
          .then(result => setPrivateEquitySponsors(result))
          .catch(error => setPrivateEquitySponsorsError(error));
      })
      .finally(() => setIsLoading(false));
  };

  const handleAddSponsor = () => {
    setIsUpdate(false);
    setUpdateValue(undefined);
    setModalActive(true);
  };
  const handleModalDismiss = () => {
    setModalActive(false);
    setIsUpdate(false);
    setUpdateValue(undefined);
  };

  const AddPrivateEquitySponsor = async (values: AddPrivateEquitySponsorValues) => {
    const command: Bixby.Models.PrivateEquitySponsorCreateRequest = values;
    PrivateEquitySponsorService.createPrivateEquitySponsors(command)
      .then(() => {
        dispatchToastSuccess('Successfully added Private Equity Sponsor');
        PrivateEquitySponsorService.getPrivateEquitySponsors()
          .then(result => setPrivateEquitySponsors(result))
          .catch(error => setPrivateEquitySponsorsError(error));
      })
      .catch(() => {
        dispatchToastError('Failed to add Private Equity Sponsor');
      })
      .finally(() => {
        handleModalDismiss();
      });
  };

  const UpdatePrivateEquitySponsor = async (values: Bixby.Models.PrivateEquitySponsor) => {
    const command: Bixby.Models.PrivateEquitySponsorUpdateRequest = values;
    PrivateEquitySponsorService.updatePrivateEquitySponsors(command)
      .then(() => {
        dispatchToastSuccess('Successfully updated Private Equity Sponsor');
        PrivateEquitySponsorService.getPrivateEquitySponsors()
          .then(result => setPrivateEquitySponsors(result))
          .catch(error => setPrivateEquitySponsorsError(error));
      })
      .catch(() => {
        dispatchToastError('Failed to update Private Equity Sponsor');
      })
      .finally(() => {
        handleModalDismiss();
      });
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="private-equity-sponsors">
        <HeadingBlock
          title={'Private Equity Sponsors'}
          addButton={{ enabled: true, text: 'Add Sponsor', onClick: handleAddSponsor }}
        />
        {privateEquitySponsorsError ? (
          <p className="error">{privateEquitySponsorsError}</p>
        ) : (
          <div className="company_owners_list">
            {privateEquitySponsors && !Bixby.Utils.isNullOrEmpty(privateEquitySponsors) ? (
              <div className="private-equity-sponsors-wrapper">
                <table className="private-equity-sponsors-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>URL</th>
                      <th>Ticker</th>
                      <th>isPublic</th>
                      <th>Edit</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {privateEquitySponsors.map((sponsor, index) => {
                      return (
                        <tr key={`private-equity-sponsor-${index}`}>
                          <td className="equity-sponsor-name">{sponsor.name}</td>
                          <td className="equity-sponsor-url" title={sponsor.url}>
                            {sponsor.url}
                          </td>
                          <td className="equity-sponsor-ticker td-center" title={sponsor.ticker}>
                            {sponsor.ticker}
                          </td>
                          <td className="td-center">{sponsor.isPublic ? 'Public' : 'Private'}</td>
                          <td className="td-center">
                            <button onClick={() => handleSponsorEdit(sponsor)} className="edit_icon_wrap">
                              <PencilIcon />
                            </button>
                          </td>
                          <td className="td-center">
                            <button className="fa fa-minus-circle" onClick={() => handleSponsorDelete(sponsor.id)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <NoDataFound dataType="owners" />
            )}
          </div>
        )}
        <Modal
          title={isUpdate ? 'Update Private Equity Sponsor' : 'Add New Private Equity Sponsor'}
          active={modalActive}
          forForm
          onClose={handleModalDismiss}>
          <AddSponsor
            isUpdate={isUpdate}
            updateValues={updateValue}
            onSubmit={AddPrivateEquitySponsor}
            onUpdate={UpdatePrivateEquitySponsor}
          />
        </Modal>
      </section>
    );
  }
};
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(null, mapDispatchToProps)(PrivateEquitySponsorsPage);
